/**
 * Small pure formatters, lifted from infostation.html.
 *
 * `slugify` deliberately lives in `lib/slug.ts` instead — it has a server-side twin that
 * owns collision handling, and keeping it here would invite someone to treat this copy as
 * authoritative.
 */

/** The local hour a new work day begins. See `workDayKey`. */
export const VIEW_DAY_START_HOUR = 4;

/**
 * The work day a timestamp belongs to, as `YYYY-MM-DD`.
 *
 * The whole per-user view-state rule (V0.24) rests on this. A work day runs **04:00 to
 * 03:59 local**, not midnight to midnight: a midnight boundary would collapse the map under
 * anyone still working in the evening, the moment the date rolled over. Shifting the
 * timestamp back by four hours makes 01:00 Tuesday still read as Monday.
 *
 * Local time on purpose — a work day is where the person is, not where the server is.
 */
export function workDayKey(ts: number): string {
  const d = new Date(ts - VIEW_DAY_START_HOUR * 3600000);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

const HTML_ESCAPES: Record<string, string> = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#39;',
};

/**
 * Escape text for interpolation into an HTML string.
 *
 * Kept for the vanilla page, which builds markup as strings. React escapes by default, so
 * a component reaching for this is a sign it is building HTML by hand — which is the thing
 * the port exists to stop.
 */
export function escapeHtml(s: unknown): string {
  return String(s).replace(/[&<>"']/g, (m) => HTML_ESCAPES[m]);
}

/**
 * Is this a URL the app will follow?
 *
 * Only http and https. The point is not tidiness: an admin types the target of an `iframe`
 * or `external` doc page by hand, and `javascript:` in that field would be stored XSS. The
 * server enforces the same rule; this is the copy that gives immediate feedback.
 */
export function isHttpUrl(u: unknown): boolean {
  const s = String(u ?? '').toLowerCase();
  return s.startsWith('http://') || s.startsWith('https://');
}

/**
 * "14/09/2026, 11:27:03" — a date and a time, in the reader's own locale.
 *
 * MOVED HERE from WidgetKeysTab when the Backups tab needed the same thing (V0.48). Its
 * comment there is worth keeping: "3 minutes ago" is not worth a library, and a date and a
 * time is what an admin actually reads off a list of backups.
 */
export function whenText(iso: string | null | undefined): string {
  if (!iso) return 'never';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? 'never' : d.toLocaleString();
}

/**
 * "28.3 MB" — a size somebody can compare against a memory stick.
 *
 * Binary units (1024), because that is what an operating system reports for the same file,
 * and a backup screen that disagrees with the file manager beside it invites the question of
 * which one is lying. One decimal place: the difference between 28.2 and 28.3 MB never
 * changes a decision, and more digits only make the number harder to read.
 */
export function bytesText(n: number | null | undefined): string {
  if (typeof n !== 'number' || !Number.isFinite(n) || n < 0) return '—';
  if (n < 1024) return `${n} B`;
  const units = ['KB', 'MB', 'GB', 'TB'];
  let v = n / 1024;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i++;
  }
  return `${v.toFixed(1)} ${units[i]}`;
}
